import type { MobilePromptCardProps } from "../types";

import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Button, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";

import { LanguageBadge } from "@/components/Prompt/LanguageBadge";

import { CollapsibleContent } from "./CollapsibleContent";

export const MobilePromptCard: React.FC<MobilePromptCardProps> = ({ prompt, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <Card className="mb-4" data-id={`prompt-card-${prompt.id}`}>
      <CardBody className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 pr-2">
            <div className="flex justify-between items-start mb-1">
              <h3 className="text-lg font-semibold truncate" data-id={`prompt-name-${prompt.id}`}>
                {prompt.name}
              </h3>
              <LanguageBadge
                className="ml-2"
                dataId={`prompt-language-badge-${prompt.id}`}
                sourceLanguage={prompt.sourceLanguage || "en-GB"}
              />
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              isIconOnly
              color="primary"
              data-id={`prompt-edit-${prompt.id}`}
              size="sm"
              variant="light"
              onPress={() => onEdit(prompt)}
            >
              <Icon className="h-4 w-4" icon="mdi:pencil" />
            </Button>
            <Button
              isIconOnly
              color="danger"
              data-id={`prompt-delete-${prompt.id}`}
              size="sm"
              variant="light"
              onPress={() => onDelete(prompt)}
            >
              <Icon className="h-4 w-4" icon="mdi:delete" />
            </Button>
          </div>
        </div>

        {prompt.description ? (
          <div className="text-sm text-gray-600 mb-2" data-id={`prompt-description-${prompt.id}`}>
            <CollapsibleContent content={prompt.description} maxLength={60} />
          </div>
        ) : (
          <p
            className="text-sm text-gray-400 italic mb-2"
            data-id={`prompt-description-${prompt.id}`}
          >
            No description
          </p>
        )}

        <div className="text-sm text-gray-500 mb-3" data-id={`prompt-content-${prompt.id}`}>
          <CollapsibleContent content={prompt.content} maxLength={90} />
        </div>

        <div className="flex flex-wrap gap-2 mb-2">
          {prompt.tags &&
            prompt.tags.map((tag, index) => (
              <Chip
                key={index}
                data-id={`prompt-tag-${prompt.id}-${index}`}
                size="sm"
                variant="flat"
              >
                {tag}
              </Chip>
            ))}
        </div>

        <div className="flex justify-end">
          {prompt.isPublic ? (
            <Chip color="success" data-id={`prompt-public-status-${prompt.id}`} size="sm">
              {t("Public")}
            </Chip>
          ) : (
            <Chip color="warning" data-id={`prompt-private-status-${prompt.id}`} size="sm">
              {t("Private")}
            </Chip>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
